import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { AuthPage } from "../components/AuthPage";
import { Banner } from "../components/ui/Banner";
import { Button } from "../components/ui/Button";
import { confirmEmailLink } from "../lib/credentials";

import styles from "./verify-email.module.css";

/**
 * Where the confirm link in a HireOS email lands, before someone sets up
 * the authenticator app their Company requires.
 */
export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const oobCode = params.get("oobCode") ?? "";
  const [status, setStatus] = useState<"checking" | "confirmed" | "dead">(
    "checking",
  );

  useEffect(() => {
    if (!oobCode) {
      setStatus("dead");
      return;
    }
    let live = true;
    Promise.resolve()
      .then(() => confirmEmailLink(oobCode))
      .then(() => live && setStatus("confirmed"))
      .catch(() => live && setStatus("dead"));
    return () => {
      live = false;
    };
  }, [oobCode]);

  if (status === "checking") {
    return (
      <AuthPage title="Confirm your email">
        <p className={styles.loading}>Loading</p>
      </AuthPage>
    );
  }

  if (status === "dead") {
    return (
      <AuthPage title="Confirm your email">
        <div className={styles.stack}>
          <Banner variant="error">
            This link has expired or has already been used.
          </Banner>
          <p className={styles.footNote}>
            <Link className={styles.footLink} to="/">
              Back to HireOS
            </Link>
          </p>
        </div>
      </AuthPage>
    );
  }

  return (
    <AuthPage
      title="Email confirmed"
      description="Go back to HireOS to set up your authenticator app."
    >
      <Button className={styles.submit} to="/">
        Continue
      </Button>
    </AuthPage>
  );
}
